import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useApproveOralExamIdVerification,
  useCancelOralExamTechnical,
  useOpenOralExamGroupBriefing,
  useOralExamDropIn,
  useRejectOralExamIdVerification,
  useStartOralExamCohort,
} from '@/common/api/endpoints/oral-exams/oral-exams';
import type { OralExamStartCohortRequest } from '@/common/api/models/oralExamStartCohortRequest';
import type { OralExamIdRejectionReason } from '@/common/api/models/oralExamIdRejectionReason';
import { applyDropInToCohortCache } from './applyDropInToCohortCache';
import { applyIdReviewToCohortCache } from './applyIdReviewToCohortCache';
import { useOralExamMutationErrorHandler } from './useOralExamMutationErrorHandler';

type IfMatchHeaders = { 'If-Match': string };

function ifMatchHeaders(etag: string | null | undefined): IfMatchHeaders | undefined {
  if (!etag) return undefined;
  return { 'If-Match': etag };
}

type ApproveArgs = {
  courseId: string;
  moduleId: string;
  taskId: string;
  bookingId: string;
  etag?: string | null;
};

type RejectArgs = ApproveArgs & {
  reasonCode: OralExamIdRejectionReason;
  note?: string;
};

type StartCohortArgs = {
  slotId: string;
  data: OralExamStartCohortRequest;
  etag?: string | null;
};

type SlotMatchArgs = {
  slotId: string;
  etag?: string | null;
};

type DropInArgs = {
  slotId: string;
  bookingId: string | null;
  rung?: 'observing' | 'visible' | 'speaking';
  etag?: string | null;
};

export function useOralExamIdReview(options: {
  stateQueryKey: readonly unknown[];
  etag?: string | null;
  viewerLecturerId?: string | null;
  onAfterSuccess?: () => void;
}) {
  const {
    stateQueryKey, etag, viewerLecturerId, onAfterSuccess,
  } = options;
  const queryClient = useQueryClient();
  const { withStaleRetry } = useOralExamMutationErrorHandler({
    stateQueryKey,
  });

  /** Fire-and-forget cohort refresh — start, briefing, technical cancel need not block callers. */
  const handleMutationSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: stateQueryKey }).catch(() => undefined);
    onAfterSuccess?.();
  }, [onAfterSuccess, stateQueryKey, queryClient]);

  const handleIdReviewMutationSuccess = useCallback(async (
    data: unknown,
    bookingId: string,
  ) => {
    await queryClient.cancelQueries({ queryKey: stateQueryKey });
    queryClient.setQueryData(stateQueryKey, (cached) => (
      applyIdReviewToCohortCache(cached, bookingId, data)
    ));
    queryClient.invalidateQueries({ queryKey: stateQueryKey });
    onAfterSuccess?.();
  }, [onAfterSuccess, stateQueryKey, queryClient]);

  const approveMutation = useApproveOralExamIdVerification({
    mutation: {
      onSuccess: (data, variables) => handleIdReviewMutationSuccess(data, variables.bookingId),
    },
  });

  const rejectMutation = useRejectOralExamIdVerification({
    mutation: {
      onSuccess: (data, variables) => handleIdReviewMutationSuccess(data, variables.bookingId),
    },
  });

  const startCohortMutation = useStartOralExamCohort({
    mutation: {
      onSuccess: handleMutationSuccess,
    },
  });

  const openGroupBriefingMutation = useOpenOralExamGroupBriefing({
    mutation: {
      onSuccess: handleMutationSuccess,
    },
  });

  const handleDropInMutationSuccess = useCallback(async (data: unknown) => {
    if (viewerLecturerId) {
      await queryClient.cancelQueries({ queryKey: stateQueryKey });
      queryClient.setQueryData(stateQueryKey, (cached) => (
        applyDropInToCohortCache(cached, data, viewerLecturerId)
      ));
    }
    queryClient.invalidateQueries({ queryKey: stateQueryKey });
    onAfterSuccess?.();
  }, [onAfterSuccess, queryClient, stateQueryKey, viewerLecturerId]);

  const dropInMutation = useOralExamDropIn({
    mutation: {
      onSuccess: handleDropInMutationSuccess,
    },
  });

  const cancelTechnicalMutation = useCancelOralExamTechnical({
    mutation: {
      onSuccess: handleMutationSuccess,
    },
  });

  const approve = useCallback((args: ApproveArgs) => withStaleRetry(
    (matchEtag) => approveMutation.mutateAsync({
      courseId: args.courseId,
      moduleId: args.moduleId,
      taskId: args.taskId,
      bookingId: args.bookingId,
      headers: ifMatchHeaders(matchEtag),
    }),
    args.etag !== undefined ? args.etag : etag,
    undefined,
    args.bookingId,
  ), [approveMutation, etag, withStaleRetry]);

  const reject = useCallback((args: RejectArgs) => withStaleRetry(
    (matchEtag) => rejectMutation.mutateAsync({
      courseId: args.courseId,
      moduleId: args.moduleId,
      taskId: args.taskId,
      bookingId: args.bookingId,
      data: {
        reasonCode: args.reasonCode,
        note: args.note ?? null,
      },
      headers: ifMatchHeaders(matchEtag),
    }),
    args.etag !== undefined ? args.etag : etag,
    undefined,
    args.bookingId,
  ), [rejectMutation, etag, withStaleRetry]);

  const startCohort = useCallback((args: StartCohortArgs) => withStaleRetry(
    (matchEtag) => startCohortMutation.mutateAsync({
      slotId: args.slotId,
      data: args.data,
      headers: ifMatchHeaders(matchEtag),
    }),
    args.etag !== undefined ? args.etag : etag,
  ), [startCohortMutation, etag, withStaleRetry]);

  const openGroupBriefing = useCallback((args: StartCohortArgs) => withStaleRetry(
    (matchEtag) => openGroupBriefingMutation.mutateAsync({
      slotId: args.slotId,
      data: args.data,
      headers: ifMatchHeaders(matchEtag),
    }),
    args.etag !== undefined ? args.etag : etag,
  ), [openGroupBriefingMutation, etag, withStaleRetry]);

  const dropIn = useCallback((args: DropInArgs) => withStaleRetry(
    (matchEtag) => dropInMutation.mutateAsync({
      slotId: args.slotId,
      data: {
        bookingId: args.bookingId,
        ...(args.rung !== undefined ? { rung: args.rung } : {}),
      },
      headers: ifMatchHeaders(matchEtag),
    }),
    args.etag !== undefined ? args.etag : etag,
  ), [dropInMutation, etag, withStaleRetry]);

  const cancelSessionTechnical = useCallback((args: SlotMatchArgs) => withStaleRetry(
    (matchEtag) => cancelTechnicalMutation.mutateAsync({
      slotId: args.slotId,
      headers: ifMatchHeaders(matchEtag),
    }),
    args.etag !== undefined ? args.etag : etag,
  ), [cancelTechnicalMutation, etag, withStaleRetry]);

  return {
    approve,
    reject,
    startCohort,
    openGroupBriefing,
    dropIn,
    cancelSessionTechnical,
    isApproving: approveMutation.isPending,
    isRejecting: rejectMutation.isPending,
    isStarting: startCohortMutation.isPending,
    isOpeningBriefing: openGroupBriefingMutation.isPending,
    isDroppingIn: dropInMutation.isPending,
    isCancellingTechnical: cancelTechnicalMutation.isPending,
  };
}
